import type { RfqDetailData, RfqHistoryRow } from "./api";
import { RFQ_HISTORY_ACTION } from "./history";
import { resolveWeight } from "./calc";

export interface RfqSheetLabels {
  title: string;
  purchaseTitle: string;
  projectAddress: string;
  projectName: string;
  code: string;
  printedAt: string;
  item: string;
  vendor: string;
  spec: string;
  quotePrice: string;
  quoteDate: string;
  negotiatedPrice: string;
  negotiatedDate: string;
  purchasePrice: string;
  purchaseDate: string;
  remark: string;
  contractQty: string;
  costPrice: string;
  customerPrice: string;
  handler: string;
  manager: string;
  vicePresident: string;
  declined: string;
  notPurchased: string;
}

export interface RfqSheetRow {
  vendor: string;
  quotePrice: number | null;
  quoteDate: string;
  negotiatedPrice: number | null;
  negotiatedDate: string;
  purchasePrice: number | null;
  purchaseDate: string;
  remark: string;
}

export interface RfqSheetGroup {
  item: string;
  spec: string;
  rows: RfqSheetRow[];
}

export interface RfqPurchaseRow {
  item: string;
  vendor: string;
  contractQty: string;
  purchasePrice: number | null;
  purchaseDate: string;
  remark: string;
}

export interface RfqSheet {
  companyName: string;
  code: string;
  projectName: string;
  projectAddress: string;
  printedAt: string;
  purchases: RfqPurchaseRow[];
  groups: RfqSheetGroup[];
}

function shortDate(value: Date | string | null | undefined): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return `${date.getMonth() + 1}/${date.getDate()}`;
}

function rocDate(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear() - 1911}.${pad(date.getMonth() + 1)}.${pad(date.getDate())}`;
}

function qtyNumber(value: number): string {
  return String(Math.round(value * 100) / 100);
}

function historyKey(row: RfqHistoryRow): string | null {
  const rfqItemId = row.detail?.rfqItemId;
  if (typeof rfqItemId !== "string" || !row.rfqQuoteId) return null;
  return `${row.rfqQuoteId}|${rfqItemId}`;
}

export function buildRfqSheet(
  data: RfqDetailData,
  companyName: string,
  labels: Pick<RfqSheetLabels, "declined" | "notPurchased">,
): RfqSheet {
  const { header, lines, vendors, quotes, awards, historyRows } = data;

  const ordered = [...historyRows].sort(
    (a, b) => new Date(a.occurredAt).getTime() - new Date(b.occurredAt).getTime(),
  );
  const quotedAt = new Map<string, string>();
  const firstNegotiation = new Map<string, RfqHistoryRow>();
  const lastNegotiation = new Map<string, RfqHistoryRow>();
  for (const row of ordered) {
    const key = historyKey(row);
    if (!key) continue;
    if (row.action === RFQ_HISTORY_ACTION.ProxyQuoteRecorded) {
      if (!quotedAt.has(key)) quotedAt.set(key, shortDate(row.occurredAt));
    } else if (row.action === RFQ_HISTORY_ACTION.ProxyNegotiationRecorded) {
      if (!firstNegotiation.has(key)) firstNegotiation.set(key, row);
      lastNegotiation.set(key, row);
    }
  }

  const purchaseDate =
    header.serverStatus === "completed"
      ? shortDate(header.reviewDate)
      : "";

  const purchases: RfqPurchaseRow[] = lines.map((line) => {
    const award = awards.find((row) => row.lineId === line.id);
    const vendor =
      award && !award.skip && award.vendorId
        ? vendors.find((row) => row.id === award.vendorId)
        : undefined;
    const current = vendor
      ? quotes
          .filter((quote) => quote.vendorId === vendor.id && quote.lineId === line.id)
          .sort((a, b) => b.round - a.round)[0]
      : undefined;
    const weight = line.priceMode === "weight" ? resolveWeight(line) : null;
    const contractQty =
      weight && weight.kg > 0
        ? `${qtyNumber(weight.kg)}(kg)`
        : `${qtyNumber(line.qty)}(${line.unit})`;
    const remarks = [line.spec || line.nominalSpec || ""];
    if (award?.skip) remarks.unshift(labels.notPurchased);

    return {
      item: line.name,
      vendor: vendor?.name ?? "",
      contractQty,
      purchasePrice: current?.supplied ? current.unitPrice : null,
      purchaseDate: vendor ? purchaseDate : "",
      remark: remarks.filter(Boolean).join("；"),
    };
  });

  const groups: RfqSheetGroup[] = lines.map((line) => {
    const award = awards.find((row) => row.lineId === line.id);
    const rows: RfqSheetRow[] = [];

    for (const vendor of vendors) {
      const current = quotes
        .filter((quote) => quote.vendorId === vendor.id && quote.lineId === line.id)
        .sort((a, b) => b.round - a.round)[0];
      if (!current && vendor.replyStatus !== "declined") continue;

      const key = `${vendor.id}|${line.id}`;
      const first = firstNegotiation.get(key);
      const last = lastNegotiation.get(key);
      const previous = first?.detail?.previousUnitPrice;
      const supplied = !!current?.supplied;
      const awarded = !award?.skip && award?.vendorId === vendor.id;

      const remarks: string[] = [];
      if (vendor.replyStatus === "declined") remarks.push(labels.declined);
      if (current?.remark) remarks.push(current.remark);

      rows.push({
        vendor: vendor.name,
        quotePrice: supplied
          ? last && typeof previous === "number"
            ? previous
            : current!.unitPrice
          : null,
        quoteDate: quotedAt.get(key) ?? "",
        negotiatedPrice: supplied && last ? current!.unitPrice : null,
        negotiatedDate: last ? shortDate(last.occurredAt) : "",
        purchasePrice: awarded && supplied ? current!.unitPrice : null,
        purchaseDate: awarded ? purchaseDate : "",
        remark: remarks.join("；"),
      });
    }

    if (rows.length === 0) {
      rows.push({
        vendor: "",
        quotePrice: null,
        quoteDate: "",
        negotiatedPrice: null,
        negotiatedDate: "",
        purchasePrice: null,
        purchaseDate: "",
        remark: "",
      });
    }
    if (award?.skip) {
      rows[0].remark = [labels.notPurchased, rows[0].remark]
        .filter(Boolean)
        .join("；");
    }

    return {
      item: line.name,
      spec: line.spec || line.nominalSpec || "",
      rows,
    };
  });

  return {
    companyName,
    code: header.code,
    projectName: header.name,
    projectAddress: header.deliveryLocation,
    printedAt: rocDate(new Date()),
    purchases,
    groups,
  };
}

function priceText(value: number | null): string {
  if (value == null) return "";
  return new Intl.NumberFormat("zh-TW", { maximumFractionDigits: 4 }).format(value);
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function columns(labels: RfqSheetLabels): string[] {
  return [
    labels.item,
    labels.vendor,
    labels.spec,
    labels.quotePrice,
    labels.quoteDate,
    labels.negotiatedPrice,
    labels.negotiatedDate,
    labels.purchasePrice,
    labels.purchaseDate,
    labels.remark,
  ];
}

function purchaseColumns(labels: RfqSheetLabels): string[] {
  return [
    labels.item,
    labels.vendor,
    labels.contractQty,
    labels.costPrice,
    labels.customerPrice,
    labels.purchasePrice,
    labels.purchaseDate,
    labels.handler,
    labels.manager,
    labels.vicePresident,
    labels.remark,
  ];
}

function headerCells(labels: string[]): string {
  return labels
    .map((label) =>
      /^[\u4e00-\u9fff]{4}$/.test(label)
        ? `<th>${label.slice(0, 2)}<br/>${label.slice(2)}</th>`
        : `<th>${escapeHtml(label)}</th>`,
    )
    .join("");
}

function fileName(sheet: RfqSheet, labels: RfqSheetLabels, ext: string): string {
  const base = [labels.title, sheet.code, sheet.projectName]
    .filter(Boolean)
    .join("_")
    .replace(/[\\/:*?"<>|]/g, "-");
  return `${base}.${ext}`;
}

export function renderRfqSheetHtml(sheet: RfqSheet, labels: RfqSheetLabels): string {
  const head = headerCells(columns(labels));
  const purchaseHead = headerCells(purchaseColumns(labels));

  const purchaseBody = sheet.purchases
    .map(
      (row) => `<tr class="sign">
            <td class="c">${escapeHtml(row.item)}</td>
            <td class="c">${escapeHtml(row.vendor)}</td>
            <td class="c">${escapeHtml(row.contractQty)}</td>
            <td></td>
            <td></td>
            <td class="c">${priceText(row.purchasePrice)}</td>
            <td class="c">${escapeHtml(row.purchaseDate)}</td>
            <td></td>
            <td></td>
            <td></td>
            <td class="c">${escapeHtml(row.remark)}</td></tr>`,
    )
    .join("");

  const body = sheet.groups
    .map((group) =>
      group.rows
        .map((row, index) => {
          const span = group.rows.length;
          const lead =
            index === 0
              ? `<td rowspan="${span}" class="c">${escapeHtml(group.item)}</td>`
              : "";
          const vendor = `<td class="c">${escapeHtml(row.vendor)}</td>`;
          const spec =
            index === 0
              ? `<td rowspan="${span}" class="c">${escapeHtml(group.spec)}</td>`
              : "";
          return `<tr>${lead}${vendor}${spec}
            <td class="c">${priceText(row.quotePrice)}</td>
            <td class="c">${escapeHtml(row.quoteDate)}</td>
            <td class="c">${priceText(row.negotiatedPrice)}</td>
            <td class="c">${escapeHtml(row.negotiatedDate)}</td>
            <td class="c">${priceText(row.purchasePrice)}</td>
            <td class="c">${escapeHtml(row.purchaseDate)}</td>
            <td>${escapeHtml(row.remark)}</td></tr>`;
        })
        .join(""),
    )
    .join("");

  return `<!doctype html><html><head><meta charset="utf-8" />
<title>${escapeHtml(fileName(sheet, labels, "pdf").replace(/\.pdf$/, ""))}</title>
<style>
  @page { size: A4 portrait; margin: 12mm 10mm; }
  * { box-sizing: border-box; }
  body { margin: 0; color: #000; font-family: "PingFang TC", "Microsoft JhengHei", "Noto Sans TC", sans-serif; font-size: 11pt; }
  .company { text-align: center; font-size: 18pt; letter-spacing: 2px; }
  .title { text-align: center; font-size: 16pt; margin: 2px 0 10px; }
  .title.second { margin-top: 18px; margin-bottom: 0; }
  .title span { border-bottom: 1px solid #000; padding: 0 4px; }
  .meta { display: flex; justify-content: space-between; align-items: flex-end; margin: 2px 0; }
  table { width: 100%; border-collapse: collapse; margin-top: 6px; }
  th, td { border: 1px solid #000; padding: 4px 4px; font-weight: normal; line-height: 1.35; overflow-wrap: anywhere; }
  th { white-space: pre-line; }
  td.c { text-align: center; }
  tr.sign td { height: 13mm; }
  tr { page-break-inside: avoid; }
  thead { display: table-header-group; }
</style></head><body>
  <div class="company">${escapeHtml(sheet.companyName)}</div>
  <div class="title"><span>${escapeHtml(labels.title)}</span></div>
  <div class="meta"><div>${escapeHtml(labels.projectAddress)}：${escapeHtml(sheet.projectAddress)}</div><div>${escapeHtml(labels.code)}：${escapeHtml(sheet.code)}</div></div>
  <div class="meta"><div>${escapeHtml(labels.projectName)}：${escapeHtml(sheet.projectName)}</div><div>${escapeHtml(labels.printedAt)}：${escapeHtml(sheet.printedAt)}</div></div>
  <table>
    <colgroup><col style="width:8.5%"/><col style="width:8.5%"/><col style="width:12%"/><col style="width:6.5%"/><col style="width:6.5%"/><col style="width:6.5%"/><col style="width:6.5%"/><col style="width:9%"/><col style="width:9%"/><col style="width:9%"/><col/></colgroup>
    <thead><tr>${purchaseHead}</tr></thead>
    <tbody>${purchaseBody}</tbody>
  </table>
  <!-- <div class="title second"><span>${escapeHtml(labels.title)}</span></div> -->
  <table style="margin-top:18px">
    <colgroup><col style="width:9%"/><col style="width:9%"/><col style="width:19%"/><col style="width:7.5%"/><col style="width:6.5%"/><col style="width:7.5%"/><col style="width:6.5%"/><col style="width:7.5%"/><col style="width:6.5%"/><col/></colgroup>
    <thead><tr>${head}</tr></thead>
    <tbody>${body}</tbody>
  </table>
</body></html>`;
}

export function printRfqSheet(sheet: RfqSheet, labels: RfqSheetLabels): void {
  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  iframe.setAttribute("aria-hidden", "true");
  document.body.appendChild(iframe);

  const doc = iframe.contentDocument;
  const win = iframe.contentWindow;
  if (!doc || !win) {
    iframe.remove();
    return;
  }
  doc.open();
  doc.write(renderRfqSheetHtml(sheet, labels));
  doc.close();

  const cleanup = () => setTimeout(() => iframe.remove(), 500);
  win.addEventListener("afterprint", cleanup);
  setTimeout(() => {
    win.focus();
    win.print();
  }, 250);
}
